import {
    DefaultCustomerAppreciationOptionsMap,
    ICustomerAppreciationOptionsMap,
    IPlanHooks,
    IPlanSelection
} from 'pages/Plans/PlanPage.types';
import { useState } from 'react';
import PlansApi from './Plans.api';
import { ICustomerAppreciationDiscount, IPlan, PlanTypeMap } from '../../types/Plan';
import { DiscountType } from '../../types/Promo';
import { IPlanSku } from '../../types/PlanSku';
import { DeviceType } from '../../types/Device';

export const usePlansHooks = (): IPlanHooks => {
    const [plans, setPlans] = useState<IPlan[]>([]);

    const [discountMap, setDiscountMap] = useState(DefaultCustomerAppreciationOptionsMap());

    const HandleLoadPlans = () => {
        PlansApi.GetPlans(HandleSetPlans, () => {});
    };

    const GetPlanSku = (
        planId: string,
        deviceType: DeviceType,
        lineNumber: number,
        onSuccess: (planSku: IPlanSku) => void,
        onFailure: () => void
    ) => {
        PlansApi.GetPlanSku(planId, deviceType, lineNumber, onSuccess, onFailure);
    };

    const HandleSetPlans = (plans: IPlan[]) => {
        const map: ICustomerAppreciationOptionsMap = DefaultCustomerAppreciationOptionsMap();

        plans.forEach((p) => {
            if ((p.customerAppreciationDiscounts?.length ?? 0) > 0) {
                for (const discount of p.customerAppreciationDiscounts) {
                    if (!discount.name) continue;

                    if (!Object.keys(map).includes(p.planType.toString())) {
                        map[p.planType as number] = [];
                    }

                    if (map[p.planType as number].includes(discount.name)) continue;

                    map[p.planType as number].push(discount.name);
                }
            }
        });

        plans.sort((a, b) => {
            if (a.sortOrder > b.sortOrder) return 1;
            else return -1;
        });

        setDiscountMap(map);
        setPlans(plans);
    };

    const GetLinePrice = (plan: IPlan, lineNumber: number): number => {
        const linePrices = plan.linePrice;

        if (linePrices.length === 1) return linePrices[0];

        if (lineNumber > linePrices.length) return linePrices[linePrices.length - 1];

        if (lineNumber <= linePrices.length) return linePrices[lineNumber - 1];

        return 0;
    };

    const CalcMultiLineDiscount = (plan: IPlan, lineNumber: number): number => {
        if (plan.linePrice.length === 1) {
            return 0;
        }

        if (plan.linePrice.length > 1) {
            return plan.linePrice[0] - plan.linePrice[lineNumber - 1];
        }

        return 0;
    };

    const GetPlanDisplayName = (plan: IPlan): string => {
        return `${plan.name} ${PlanTypeMap[plan.planType]}`;
    };

    const HasCustomerAppreciationDiscount = (planSelection: IPlanSelection): boolean => {
        return (planSelection.customerAppreciationDiscount &&
            !planSelection.customerAppreciationDiscount.exclusiveDiscount) as boolean;
    };

    const GetCustomerAppreciationDiscountAmountString = (planSelection: IPlanSelection): string => {
        if (planSelection.customerAppreciationDiscount) {
            switch (planSelection.customerAppreciationDiscount.discountType) {
                case DiscountType.Percent:
                    return `${planSelection.customerAppreciationDiscount.discountAmount}%`;
                case DiscountType.FlatAmount:
                    return `$${planSelection.customerAppreciationDiscount.discountAmount.toFixed(
                        2
                    )}`;
            }
        }

        return '';
    };

    const GetDiscountByName = (
        plan: IPlan,
        name: string
    ): ICustomerAppreciationDiscount | undefined => {
        return plan.customerAppreciationDiscounts.filter((c) => c.name === name)[0];
    };

    return {
        discountMap,
        plans,
        HandleLoadPlans,
        GetLinePrice,
        CalcMultiLineDiscount,
        GetPlanDisplayName,
        HasCustomerAppreciationDiscount,
        GetCustomerAppreciationDiscountAmountString,
        GetDiscountByName,
        GetPlanSku
    };
};
